import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Footer = () => {
  const navigate = useNavigate();
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-black to-red-950 text-white relative overflow-hidden">
      {/* Grain Overlay */}
      <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noise)"/%3E%3C/svg%3E")'
      }}></div>

      {/* Sparkle Particles - Hidden on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden sm:block">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-red-500 rounded-full"
            initial={{ x: Math.random() * 100 + '%', y: Math.random() * 100 + '%', opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 3 }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 relative z-10">
        {/* Mobile: Stack layout, Desktop: Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
          {/* Company Info - Full width on mobile */}
          <div className="col-span-2">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
              MP Frankendach
            </h2>
            <p className="text-gray-300 text-sm sm:text-base mb-4 sm:mb-6 leading-relaxed">
              Ihr zuverlässiger Partner für Dachdeckerarbeiten in Fürth. Über 10 Jahre Erfahrung.
            </p>
            <div className="flex space-x-3">
              <SocialIcon icon={<Facebook />} link="https://www.facebook.com/profile.php?id=61574644291943" />
              <SocialIcon icon={<Instagram />} link="https://www.instagram.com/mpfrankendach" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-red-400">
              Quick Links
            </h3>
            <ul className="space-y-2">
              <FooterLink onClick={() => scrollToSection('hero')}>Startseite</FooterLink>
              <FooterLink onClick={() => scrollToSection('leistungen')}>Leistungen</FooterLink>
              <FooterLink onClick={() => scrollToSection('referenzen')}>Referenzen</FooterLink>
              <FooterLink onClick={() => scrollToSection('kontakt')}>Kontakt</FooterLink>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-red-400">
              Kontakt
            </h3>
            <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base">
              <li className="flex items-center space-x-2">
                <Phone className="text-red-500 flex-shrink-0" size={16} />
                <a href="tel:+4991198012850" className="text-gray-300 hover:text-red-400 transition-colors">
                  0911 - 980 12 850
                </a>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="text-red-500 flex-shrink-0" size={16} />
                <a href="tel:+491733666776" className="text-gray-300 hover:text-red-400 transition-colors">
                  +49 173 3666776
                </a>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="text-red-500 flex-shrink-0" size={16} />
                <a href="mailto:info@mp-frankendach.de" className="text-gray-300 hover:text-red-400 transition-colors truncate">
                  info@mp-frankendach.de
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <MapPin className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                <span className="text-gray-300 text-sm">
                  Schwabacher Str. 260<br />90763 Fürth
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 sm:pt-8 border-t border-gray-800">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
            <p className="text-gray-400 text-xs sm:text-sm text-center sm:text-left">
              © {new Date().getFullYear()} MP Frankendach. Alle Rechte vorbehalten.
            </p>
            <div className="flex space-x-4 sm:space-x-6 text-xs sm:text-sm">
              <button onClick={() => navigate('/impressum')} className="text-gray-400 hover:text-red-400 transition-colors">
                Impressum
              </button>
              <button onClick={() => navigate('/datenschutz')} className="text-gray-400 hover:text-red-400 transition-colors">
                Datenschutz
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterLink = ({ children, onClick }) => (
  <li>
    <button
      onClick={onClick}
      className="text-gray-300 hover:text-red-400 transition-colors text-left text-sm sm:text-base"
    >
      {children}
    </button>
  </li>
);

const SocialIcon = ({ icon, link }) => (
  <motion.a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    className="p-2.5 sm:p-3 bg-white/10 backdrop-blur-md rounded-lg hover:bg-red-600 transition-colors"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
  >
    {React.cloneElement(icon, { size: 18, className: 'sm:w-5 sm:h-5' })}
  </motion.a>
);
